// Typed wrappers for core-api's /admin/* endpoints (ADR-0024).
//
// Mirrors the shape of endpoints/auth.ts. Every call is authenticated
// (RequirePlatformAdmin + req.requirePlatformAdmin() on the server)
// so no skipAuth here. The xlsx preview is the project's first
// multipart surface — see client.ts's FormData branch.

import type {
  AdminBrandCreateRequest,
  AdminBrandCreateResponse,
  AdminBrandLogoCommitRequest,
  AdminBrandLogoCommitResponse,
  AdminBrandLogoUploadUrlRequest,
  AdminBrandLogoUploadUrlResponse,
  AdminOrgCreateRequest,
  AdminOrgCreateResponse,
  AdminOrgAssetsResponse,
  AdminOrgDeleteRequest,
  AdminOrgDeleteResponse,
  AdminOrgDefaultBrandUpdateRequest,
  AdminOrgDefaultBrandUpdateResponse,
  AdminOrgDetailResponse,
  AdminOrgInvitationCreateRequest,
  AdminOrgInvitationCreateResponse,
  AdminOrgInvitationsRetryFailedResponse,
  AdminOrgListResponse,
  AdminOrgLogoCommitRequest,
  AdminOrgLogoCommitResponse,
  AdminOrgLogoUploadUrlRequest,
  AdminOrgLogoUploadUrlResponse,
  AdminOrgMembersResponse,
  AdminOrgPatchRequest,
  AdminOrgPatchResponse,
  AdminOrgReportsResponse,
  AdminRecipeListResponse,
  AdminWorkbookCommitRequest,
  AdminWorkbookCommitResponse,
  AdminWorkbookPreviewResponse,
} from "@bitacorapro/api-types";
import { request, requestBlob } from "../client";

export type WorkbookPreviewArgs = {
  file: File;
  orgName: string;
  recipeSlug: string;
  rut?: string;
};

export const adminApi = {
  listRecipes: (): Promise<AdminRecipeListResponse> =>
    request<AdminRecipeListResponse>("GET", "core", "/admin/recipes"),
  listOrgs: (): Promise<AdminOrgListResponse> =>
    request<AdminOrgListResponse>("GET", "core", "/admin/orgs"),
  getOrg: (id: string): Promise<AdminOrgDetailResponse> =>
    request<AdminOrgDetailResponse>("GET", "core", `/admin/orgs/${id}`),
  createOrg: (body: AdminOrgCreateRequest): Promise<AdminOrgCreateResponse> =>
    request<AdminOrgCreateResponse>("POST", "core", "/admin/orgs", { body }),
  /** PATCH the active flag (soft disable / re-enable). */
  patchOrg: (id: string, body: AdminOrgPatchRequest): Promise<AdminOrgPatchResponse> =>
    request<AdminOrgPatchResponse>("PATCH", "core", `/admin/orgs/${id}`, { body }),
  /** Hard cascade delete. confirmationName must equal the org name verbatim. */
  deleteOrg: (id: string, body: AdminOrgDeleteRequest): Promise<AdminOrgDeleteResponse> =>
    request<AdminOrgDeleteResponse>("DELETE", "core", `/admin/orgs/${id}`, { body }),
  /** Re-fan invitations for jefe + supervisor emails missing from auth_invitations.
   *  Managers-sheet rows aren't covered — use the standard invitation form. */
  retryFailedInvitations: (id: string): Promise<AdminOrgInvitationsRetryFailedResponse> =>
    request<AdminOrgInvitationsRetryFailedResponse>(
      "POST",
      "core",
      `/admin/orgs/${id}/invitations/retry-failed`
    ),

  /** Flat list of an org's Tiendas (assets) for the supervisor store picker (ADR-0047). */
  listOrgAssets: (id: string): Promise<AdminOrgAssetsResponse> =>
    request<AdminOrgAssetsResponse>("GET", "core", `/admin/orgs/${id}/assets`),

  /** Per-org report roster for the support-access entry point (#0487,
   *  core-api route #0494). Backed by adminOrgReportRowSchema
   *  (@bitacorapro/api-types) — a .pick()-derived, security/compliance-
   *  ruled subset of reportSchema (ADR-0094 §10 / Postscript 3). Treat
   *  `folio` as possibly absent (undefined until signed), and never
   *  assume any field beyond what the shared schema declares. */
  listOrgReports: (id: string): Promise<AdminOrgReportsResponse> =>
    request<AdminOrgReportsResponse>("GET", "core", `/admin/orgs/${id}/reports`),

  /** Platform-admin single-person invite. For a facility_supervisor with
   *  assetIds, core-api stamps the selected stores so the supervisor binds
   *  to them on acceptance (ADR-0047). Routes through core-api (not auth-svc
   *  directly) so the asset stamp and the invitation fan-out stay atomic. */
  createOrgInvitation: (
    id: string,
    body: AdminOrgInvitationCreateRequest
  ): Promise<AdminOrgInvitationCreateResponse> =>
    request<AdminOrgInvitationCreateResponse>("POST", "core", `/admin/orgs/${id}/invitations`, {
      body,
    }),

  // xlsx ingest — two-step. Preview parses + stores a draft; commit
  // echoes draftId + sha256 to apply.
  previewWorkbook: (args: WorkbookPreviewArgs): Promise<AdminWorkbookPreviewResponse> => {
    const form = new FormData();
    // Text fields BEFORE the file part — @fastify/multipart populates
    // `file.fields` only with fields that arrived before the file in
    // the multipart stream. File last so the server sees orgName +
    // recipeSlug + rut when it calls req.file().fields[…].
    form.set("orgName", args.orgName);
    form.set("recipeSlug", args.recipeSlug);
    if (args.rut) form.set("rut", args.rut);
    form.set("file", args.file);
    return request<AdminWorkbookPreviewResponse>("POST", "core", "/admin/workbook/preview", {
      body: form,
    });
  },
  commitWorkbook: (body: AdminWorkbookCommitRequest): Promise<AdminWorkbookCommitResponse> =>
    request<AdminWorkbookCommitResponse>("POST", "core", "/admin/workbook/commit", { body }),
  downloadEmptyTemplate: (): Promise<Blob> =>
    requestBlob("GET", "core", "/admin/workbook/template"),

  // Members are served by auth-svc per ADR-0033 §Decision 3 — the
  // dashboard composes the org detail (core-api) and the roster
  // (auth-svc) in parallel from two separate queries.
  getOrgMembers: (id: string): Promise<AdminOrgMembersResponse> =>
    request<AdminOrgMembersResponse>("GET", "auth", `/admin/orgs/${id}/members`),

  // Brand-logo flow is three steps on the wire (request URL → PUT
  // directly to R2 → commit). Steps 1 and 3 hit core-api; step 2 is
  // an unauthenticated PUT against the presigned URL (the signature
  // is in the query string — sending Authorization would invalidate
  // it). The orchestrating component owns that fetch directly.
  requestBrandLogoUploadUrl: (
    orgId: string,
    brandId: string,
    body: AdminBrandLogoUploadUrlRequest
  ): Promise<AdminBrandLogoUploadUrlResponse> =>
    request<AdminBrandLogoUploadUrlResponse>(
      "POST",
      "core",
      `/admin/orgs/${orgId}/brands/${brandId}/logo/upload-url`,
      { body }
    ),
  commitBrandLogo: (
    orgId: string,
    brandId: string,
    body: AdminBrandLogoCommitRequest
  ): Promise<AdminBrandLogoCommitResponse> =>
    request<AdminBrandLogoCommitResponse>(
      "POST",
      "core",
      `/admin/orgs/${orgId}/brands/${brandId}/logo/commit`,
      { body }
    ),

  // The organization's own corporate logo (ADR-0164) — the same
  // three-step wire flow as the brand logo above, on its own pair of
  // routes. features/admin/logoUpload.ts orchestrates both.
  requestOrgLogoUploadUrl: (
    orgId: string,
    body: AdminOrgLogoUploadUrlRequest
  ): Promise<AdminOrgLogoUploadUrlResponse> =>
    request<AdminOrgLogoUploadUrlResponse>("POST", "core", `/admin/orgs/${orgId}/logo/upload-url`, {
      body,
    }),
  commitOrgLogo: (
    orgId: string,
    body: AdminOrgLogoCommitRequest
  ): Promise<AdminOrgLogoCommitResponse> =>
    request<AdminOrgLogoCommitResponse>("POST", "core", `/admin/orgs/${orgId}/logo/commit`, {
      body,
    }),

  /** Marks one brand as the org's default (`brands.is_default`, ADR-0164). */
  setDefaultBrand: (
    orgId: string,
    body: AdminOrgDefaultBrandUpdateRequest
  ): Promise<AdminOrgDefaultBrandUpdateResponse> =>
    request<AdminOrgDefaultBrandUpdateResponse>(
      "PUT",
      "core",
      `/admin/orgs/${orgId}/default-brand`,
      {
        body,
      }
    ),

  createBrand: (orgId: string, body: AdminBrandCreateRequest): Promise<AdminBrandCreateResponse> =>
    request<AdminBrandCreateResponse>("POST", "core", `/admin/orgs/${orgId}/brands`, { body }),
};
